define([
	"dojo/_base/declare",
	"csi",
	"csi/action/ActionView",
	"csi/def",
	"csi/locale",
	"csi/reflection",
	"csi/xml",
	"./NewAllocatedSupplierRequestsForm",
	"./NewSupplierRequest"
], function(declare,csi,ActionView,def,locale,reflection,xml,NewAllocatedSupplierRequestsForm,NewSupplierRequest) {

	return declare(NewSupplierRequest, {
		isAllocatedAction: true,

		fixedType: "DataPackageContainerType:Style",

		productField: "Styles",

		/** @override Boolean: default New controller only adds one node - this controller may add many */
		bUpdateParent: false,

		/** @override Boolean: validation is handled explicitly on the server side */
		bValidate: false,

		/** @override  Boolean: show progress bar */
		bShowProgress: true,

		// String[]: URLs of newly created SRs
		urlNewItems: null,

		TYPE: "SupplierRequest",

		// Boolean: To know if the action is from New SRM or from existing SRM
		isFromNewSRM: false,

		// Boolean: Set on NewAllocatedSupplierRequests.js. To know if the action is from Hierarchy or from a Product
		isFromHierarchy: false,

		// C8Node: SRM node if the action is inside a SRM - Set on NewAllocatedSupplierRequestsStore
		srMaster: null,

		/**
	     * @override
	     * disable Save & Go since action can create multiple SR
	     */
		_canNavigate: function() {
			return false;
		},

		/**
		 * @override
		 * Disallow Save & New in this case
		 */
		_canRepeat: function() {
			return false;
		},

		/** @override */
		toWriter: function() {
			this.srMaster = this.srMaster || this.m_cache.getNode(this.getFormData().getFieldValue("SRMaster"));
			const fd = this.m_formData;
			this.products = fd.getFieldValue(this.productField, true);

			const w = this.toWriterAllocatedSR(fd);
			return w;
		},

		/**
		 * Create the Call to Create/Update SRM and add the one or many new SR to it
		 * @param {FormData} fd - SR form data
		 */
		toWriterAllocatedSR(fd) {
			this.isFromNewSRM = fd.getFieldValue("CreateOption", true) === "0";
			const module = "Sourcing";
			const createUpdateSRMOperation = this.isFromNewSRM ? "CreateSupplierRequestMaster" :  "UpdateSupplierRequestMaster";
			let masterURl = this.createNewUrl();
			const subtype = fd.getFieldValue("Subtype", true);
			const sources = this.getSourceUrlsBySubtype(subtype, fd);
			const writer = this.m_writer;

			const srMasterCall = writer.callMethod(module, createUpdateSRMOperation);
			if(this.isFromNewSRM) {
				srMasterCall.addParameter("MasterUrl", xml.ATTR_REF, masterURl)
				.addParameter("MasterName", xml.ATTR_STRING, fd.getFieldValue("SRMasterName", true))
				.addParameter("RequestType", xml.ATTR_ENUM, this.fixedType)
				.addParameter("SRSubtype", xml.ATTR_REF, subtype);
				const parentStructureItem = this.getParentStructureItem();
				if (parentStructureItem) {
					srMasterCall.addParameter("ParentStructureItem", xml.ATTR_REF, parentStructureItem);
				}
			} else {
				masterURl = fd.getFieldValue("SRMaster", true);
				srMasterCall.addParameter("URL", xml.ATTR_REF, masterURl)
			}

			srMasterCall.addParameter("Products", xml.ATTR_REFSET, this.products)
			.addParameter("SRMTemplate", xml.ATTR_REF, fd.getFieldValue("SRTemplate", true))
			.addParameter("Sources", xml.ATTR_REFSET, sources)
			.addParameter("UpdateExistingSupplierRequests", xml.ATTR_BOOLEAN, fd.getFieldValue("UpdateExistingSupplierRequests", true));

			const createSupplierRequestPerSource =  "CreateSupplierRequestInMaster";
			let createSRsCall = writer.callList(module, createSupplierRequestPerSource,sources, "Source");
			this.addParameterstoSRCall(createSRsCall, fd, subtype, [], this.products)
			.addParameter("ParentURL", xml.ATTR_REF, masterURl);

			fd.getFields()
				.filter((field) => !field.m_bUsed)
				.forEach((field) => {
					const fieldId = field.idParameter || field.m_id;
					createSRsCall.addGeneralizedParameter(fieldId, field.getType(), field.getValue());
					field.m_bUsed = true;
				});

			return createSRsCall;
		},

		/** 
		 * @override
		 * The new Elements are comming in the ModifiedUrls, so we need to add them to the request
		 */
		getReturnNodeRequest: function(/*String[]*/urlsNew) {
			if(this.m_response) {
				return this.inherited(arguments, [urlsNew.concat(this.m_response.getModifiedUrls())]);
			}
			return this.inherited(arguments);
		},

		/** 
		 * @override
		 * Get newly created Supplier Requests
		 */
		onPublishResponse(response) {
			if(response) {
				const modifiedUrls = csi.sort(response.getNodesOfType(this.TYPE), "$URL");
				this.urlNewItems = modifiedUrls?.filter(nodeURL => !this.srMaster?.SRMSupplierRequests?.includes(nodeURL.$URL));
			}
		},

		/**
		 * @override
		 * Get New nodes resulting from urlNewItems 
		 */
		getNewNodes: function() {
			return this.urlNewItems;
		},

		/** @override */
		getSuccess: function() {
			const newSRCount = this.urlNewItems ? this.urlNewItems.length : 0;
			const typeSuccess = newSRCount > 1 ? locale.typePlural(this.TYPE): locale.type(this.TYPE);
			if(this.isFromNewSRM) {
				return locale.resource("NewAllocatedSupplierRequests_Master_Success", [newSRCount, typeSuccess]);
			} else {
				return locale.resource("NewAllocatedSupplierRequests_Success", [newSRCount,typeSuccess]);
			}
		},

		/**
		 * @override - from NewSupplierRequest.js
		 * to handle the case when SR is created inside a Supplier Request Master
		 * @returns {string} parentStructureItem - parent structure item url
		 */
		getParentStructureItem () {
			if(this.srMaster) {
				return this.getSRParentStructureItemFromMaster(this.srMaster);
			} else {
				return this.inherited(arguments);
			}
		},

		/**
		 * @param {string} master - SRM node
		 * @returns {string} parentStructureItem - parent structure item url
		 */
		getSRParentStructureItemFromMaster(master) {
			if(master.RequestType !== this.fixedType) {
				return null;
			}
			const masterHierarchyOptions = ["SRMCollection", "SRMCategory2", "SRMCategory1", "SRMSeason"];
			const parentStructureItem = masterHierarchyOptions.find((hierarchyOption) => {
				if(xml.isValidURL(master[hierarchyOption])) {
					return true;
				}
			});
			return master[parentStructureItem];
		},
	});
});